/**
 * User-facing message text for the add-in, kept in one place so wording can be reviewed and
 * changed without hunting through components. Mirrors `APIResponse` on the BuilderMetrix side.
 *
 * These are FALLBACKS. When BuilderMetrix reports an error, that message is what the add-in
 * shows — see `api/apiError.ts`. The text here is used only when a response carries no usable
 * message of its own (an empty body, an HTML error page, or a .NET placeholder).
 */

export const ErrorMessages = {
  /** Nothing usable in the error and no status code to describe. */
  Unexpected: "An unexpected error occurred.",
  Offline: "Could not reach BuilderMetrix. Check your network connection.",

  // Sign-in. The provider's own reason goes to the console, not the task pane.
  SignInFailed: "Sign-in failed. Please try again or contact your administrator.",
  PopupBlocked: "Popup blocked. Please allow popups and try again.",

  // Reference data loaded into the Template tab.
  LoadEntityTypesFailed: "Could not load entity types from BuilderMetrix.",
  LoadRolesFailed: "Could not load roles from BuilderMetrix.",
  LoadFiltersFailed: "Could not load filters from BuilderMetrix.",
  LoadColumnsFailed: "Could not load columns from BuilderMetrix.",
  LoadUdfsFailed: "Could not load user-defined fields from BuilderMetrix.",
  LoadReportsFailed: "Could not load reports from BuilderMetrix.",
  LoadConnectionsFailed: "Could not load database connections from BuilderMetrix.",
  LoadUserFailed: "Could not load your BuilderMetrix user details.",
  LoadWorkbookConfigFailed: "Could not read the template configuration from this workbook.",

  // Template operations.
  TemplateSaveFailed: "The template could not be saved.",
  TemplateRemoveFailed: "The template could not be removed.",
  TemplateOpenFailed: "Could not open the template.",

  // Saved report (Excel file) operations.
  ReportOpenFailed: "Could not open the saved report.",
  ReportRemoveFailed: "The saved report could not be removed.",
  DownloadFailed: "The download failed.",

  AiAgentFailed: "The AI agent could not answer that request.",
} as const;

/**
 * Plain descriptions of an HTTP status, used when the response body carries no message.
 * Keyed by status code; anything absent falls back to {@link unexpectedStatusMessage}.
 */
export const StatusMessages: Readonly<Record<number, string>> = {
  400: "BuilderMetrix rejected the request as invalid.",
  401: "Your session has expired. Sign in again to continue.",
  403: "You do not have permission to perform this action.",
  404: "The requested item no longer exists in BuilderMetrix.",
  408: "BuilderMetrix took too long to respond. Please try again.",
  409: "This change conflicts with existing BuilderMetrix data.",
  413: "This workbook is too large to upload.",
  422: "BuilderMetrix could not process the submitted values.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "BuilderMetrix reported an internal error.",
  502: "BuilderMetrix is temporarily unavailable. Please try again shortly.",
  503: "BuilderMetrix is temporarily unavailable. Please try again shortly.",
  504: "BuilderMetrix took too long to respond. Please try again.",
} as const;

export function unexpectedStatusMessage(status: number): string {
  return `BuilderMetrix returned an unexpected response (${status}).`;
}

/**
 * Shown when a template name is already taken. BuilderMetrix reports this as a 409 naming the EF
 * entity and column list ("BMXService already exists with the same value for Name, TypeId."),
 * which is accurate but unusable in a task pane.
 */
export function duplicateTemplateNameMessage(templateName: string): string {
  return `A template named "${templateName}" already exists. Choose a different name.`;
}
